/**
 * Shared row header for the assign / escalate drawer.
 *
 * Renders the horizontal content shared by an available-list row
 * (`ResourceRowItem`) and a selected card (`SelectedMemberCard`):
 *   avatar + name + right-cluster(work badges, AddSchedule, CheckCircle).
 *
 * The bordered container (border colour, padding, click-to-select / vertical
 * stack with time rows) stays with each caller — only this inner header is
 * shared, so the two states visually "morph" through resting → hover →
 * selected → schedule-added.
 *
 * Reveal rules (redesign):
 *  - Work badges (`.member-row__meta`): hidden at rest, revealed by the
 *    CONTAINER's CSS — hover for available rows, always-on for selected cards.
 *  - Action icons (AddSchedule + CheckCircle): ALWAYS rendered to reserve
 *    width (no layout jump), made visible only when `isSelected`.
 */

import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Avatar, Tooltip } from "components";
import { AddScheduleIcon, CheckCircleIcon } from "components/icons";
import type { ResourceRow } from "../types";

// Backend casing for `resource_work_type` varies (e.g. "remote", "onsite");
// normalize to title-case for display.
const formatWorkType = (value?: string | null): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

interface MemberRowHeaderProps {
  resource: ResourceRow;
  // Drives action-icon visibility + CheckCircle colour.
  isSelected: boolean;
  // Off for selection-only mode (recurring) — no AddScheduleIcon at all.
  schedulingEnabled: boolean;
  // ≥1 time row present → AddScheduleIcon gets the active pill treatment AND
  // its click clears all rows (adding more is the card-level "+" button's job).
  hasSchedules: boolean;
  // Already-escalated read-only card: muted content, non-interactive circle.
  muted?: boolean;
  fullName: string;
  // Header-level schedule toggle: adds the first row when none exist, clears
  // all rows when some do. Omitted for available/muted rows.
  onToggleSchedule?: () => void;
  // Deselects the member. Omitted for available rows (the whole row toggles)
  // and muted rows (non-interactive).
  onDeselect?: () => void;
}

export const MemberRowHeader = ({
  resource,
  isSelected,
  schedulingEnabled,
  hasSchedules,
  muted = false,
  fullName,
  onToggleSchedule,
  onDeselect,
}: MemberRowHeaderProps) => {
  const workType = formatWorkType(resource.resource_work_type);
  const workRole = resource.resource_work_role ?? "";
  const visibility = isSelected ? "visible" : "hidden";

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ gap: "8px", ...(muted && { opacity: 0.5 }) }}
    >
      <Avatar fullname={fullName} src={resource.profile_image ?? undefined} />
      <Typography variant="subtitle1" sx={{ flex: 1 }}>
        {fullName}
      </Typography>
      <Stack direction="row" alignItems="center" sx={{ gap: "8px" }}>
        {(workType || workRole) && (
          <Stack
            direction="row"
            alignItems="center"
            className="member-row__meta"
            sx={{ gap: "8px" }}
          >
            {workType && <Badge label={workType} />}
            {workRole && <Badge label={workRole} />}
          </Stack>
        )}
        {schedulingEnabled && !muted && (
          <AddScheduleButton
            hasSchedules={hasSchedules}
            visibility={visibility}
            onToggleSchedule={onToggleSchedule}
          />
        )}
        <SelectCircleButton
          fullName={fullName}
          isSelected={isSelected}
          muted={muted}
          visibility={visibility}
          onDeselect={onDeselect}
        />
      </Stack>
    </Stack>
  );
};

type Visibility = "visible" | "hidden";

const AddScheduleButton = ({
  hasSchedules,
  visibility,
  onToggleSchedule,
}: {
  hasSchedules: boolean;
  visibility: Visibility;
  onToggleSchedule?: () => void;
}) => {
  const theme = useTheme();
  // The padded pill container is ALWAYS present (constant size → no layout
  // jump). Three states:
  //   default  (no rows)        → transparent bg, dark icon
  //   hover    (no rows)        → neutral["005"] bg, dark icon, "Schedule" tip
  //   selected (≥1 row present) → neutral["010"] bg, primary icon; click clears
  const restBg = hasSchedules ? theme.palette.neutral["010"] : "transparent";
  const hoverBg = hasSchedules
    ? theme.palette.neutral["010"]
    : theme.palette.neutral["005"];
  const button = (
    <IconButton
      aria-label={hasSchedules ? "Clear all schedules" : "Add schedule"}
      disabled={!onToggleSchedule}
      onClick={(e) => {
        e.stopPropagation();
        onToggleSchedule?.();
      }}
      sx={{
        p: "4px",
        borderRadius: "4px",
        bgcolor: restBg,
        color: hasSchedules
          ? theme.palette.primary.main
          : theme.palette.lightBg.high,
        visibility,
        "&:hover": { bgcolor: hoverBg },
      }}
    >
      <AddScheduleIcon sx={{ fontSize: "16px" }} />
    </IconButton>
  );
  // Tooltip only on the interactive (selected) card — wrapping the disabled,
  // width-reserving button on available rows would warn AND show a phantom tip
  // over empty space.
  return onToggleSchedule ? (
    <Tooltip title="Schedule" disableSentenceCase>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const SelectCircleButton = ({
  fullName,
  isSelected,
  muted,
  visibility,
  onDeselect,
}: {
  fullName: string;
  isSelected: boolean;
  muted: boolean;
  visibility: Visibility;
  onDeselect?: () => void;
}) => {
  const theme = useTheme();
  return (
    <IconButton
      aria-label={isSelected ? `Deselect ${fullName}` : `Select ${fullName}`}
      disabled={!onDeselect}
      onClick={(e) => {
        e.stopPropagation();
        onDeselect?.();
      }}
      sx={{
        p: 0,
        color: muted ? theme.palette.darkBg.low : theme.palette.primary.main,
        visibility,
        "&:hover": { bgcolor: "transparent" },
      }}
    >
      <CheckCircleIcon sx={{ fontSize: "24px" }} />
    </IconButton>
  );
};

// Work-type / work-role pill.
const Badge = ({ label }: { label: string }) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        p: "4px 8px",
        bgcolor: theme.palette.neutral["010"],
        borderRadius: "4px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="caption-2"
        sx={{ color: theme.palette.lightBg.medium }}
      >
        {label}
      </Typography>
    </Stack>
  );
};
